import React, { useState } from 'react';
import {
  X, CheckCircle, Circle, ExternalLink, ArrowRight, ArrowLeft,
  Copy, Sparkles, Clock, ChevronRight
} from 'lucide-react';

/**
 * Guided flow definities per template_id.
 * Elke flow heeft stappen met: titel, beschrijving, actie (link/kopieer/check), en tips.
 */
const GUIDED_FLOWS = {
  geen_gbp: {
    title: 'Google Bedrijfsprofiel Aanmaken',
    subtitle: 'In 5 stappen zichtbaar op Google Maps',
    estimated_time: '15-20 minuten + verificatietijd',
    steps: [
      {
        title: 'Google Bedrijfsprofiel openen',
        description: 'Ga naar Google Business en log in met uw Google account. Als u nog geen Google account heeft, maak dan eerst een aan.',
        action: { type: 'link', url: 'https://business.google.com/', label: 'Open Google Business' },
        tip: 'Gebruik het Google account dat u ook voor uw bedrijf wilt gebruiken.',
      },
      {
        title: 'Bedrijfsnaam invoeren',
        description: 'Voer uw officiële bedrijfsnaam in precies zoals uw klanten u kennen. Voeg geen extra zoekwoorden toe — Google kan uw profiel verwijderen als u dat doet.',
        action: { type: 'copy', field: 'company' },
        tip: 'Gebruik de naam zoals die op uw KvK-registratie staat.',
      },
      {
        title: 'Categorie kiezen',
        description: 'Kies de categorie die het beste bij uw bedrijf past. U kunt later extra categorieën toevoegen.',
        action: { type: 'copy', field: 'branche' },
        tip: 'Begin met typen en kies de meest specifieke optie. Bijv. "Rijschool" in plaats van "School".',
      },
      {
        title: 'Adres en servicegebied invullen',
        description: 'Voer uw bedrijfsadres in. Als u klanten op locatie bezoekt, kunt u ook een servicegebied instellen zonder uw adres te tonen.',
        action: { type: 'copy', field: 'locatie' },
        tip: 'Als u een thuisbedrijf heeft, kunt u kiezen om alleen een servicegebied te tonen zonder uw huisadres.',
      },
      {
        title: 'Verificatie starten',
        description: 'Google stuurt een verificatiecode (per post, telefoon of e-mail). Dit kan 5-14 dagen duren per post. Telefonische verificatie is sneller.',
        action: { type: 'check' },
        tip: 'Kies telefoonverificatie als die optie beschikbaar is — dat is het snelst.',
      },
    ],
  },
  gbp_optimaliseren: {
    title: 'Google Bedrijfsprofiel Optimaliseren',
    subtitle: 'Scoor hoger in Google Maps met een compleet profiel',
    estimated_time: '30-45 minuten',
    steps: [
      {
        title: 'Profiel openen in Google Business',
        description: 'Log in bij Google Business en open uw bestaande bedrijfsprofiel.',
        action: { type: 'link', url: 'https://business.google.com/', label: 'Open Google Business' },
      },
      {
        title: 'Beschrijving toevoegen',
        description: 'Schrijf een bedrijfsbeschrijving van 250-750 tekens. Gebruik uw belangrijkste zoekwoorden natuurlijk in de tekst.',
        action: { type: 'copy', field: 'gbp_beschrijving' },
        tip: 'Noem uw branche, locatie en belangrijkste diensten in de eerste 2 zinnen.',
      },
      {
        title: 'Foto\'s uploaden (minimaal 10)',
        description: 'Voeg professionele foto\'s toe: gevel, interieur, team, producten/diensten, en logo. Bedrijven met meer dan 10 foto\'s krijgen 42% meer routebeschrijvingen.',
        action: { type: 'check' },
        tip: 'Gebruik horizontale foto\'s (landscape) van minimaal 720x720 pixels.',
      },
      {
        title: 'Openingstijden instellen',
        description: 'Stel uw reguliere openingstijden in en voeg speciale openingstijden toe voor feestdagen.',
        action: { type: 'check' },
      },
      {
        title: 'Diensten/producten toevoegen',
        description: 'Voeg al uw diensten of producten toe met beschrijvingen en prijzen. Dit helpt Google begrijpen wat u aanbiedt.',
        action: { type: 'check' },
        tip: 'Voeg minimaal 5 diensten toe met een korte beschrijving per stuk.',
      },
      {
        title: 'Eerste bericht plaatsen',
        description: 'Plaats een update-bericht over een aanbieding, nieuws of tip. Actieve profielen scoren beter.',
        action: { type: 'check' },
        tip: 'Plan om wekelijks een bericht te plaatsen — dit houdt uw profiel actief.',
      },
    ],
  },
  geen_analytics: {
    title: 'Google Analytics 4 Installeren',
    subtitle: 'Begin met het meten van uw websiteverkeer',
    estimated_time: '20-30 minuten',
    steps: [
      {
        title: 'Google Analytics account aanmaken',
        description: 'Ga naar Google Analytics en maak een gratis account aan met uw Google account.',
        action: { type: 'link', url: 'https://analytics.google.com/', label: 'Open Google Analytics' },
      },
      {
        title: 'Property aanmaken',
        description: 'Klik op "Beheerder" → "Property maken". Voer uw bedrijfsnaam in als property naam en selecteer uw tijdzone (Europa/Amsterdam).',
        action: { type: 'copy', field: 'company' },
      },
      {
        title: 'Gegevensstroom toevoegen',
        description: 'Kies "Web" als platform. Voer uw website URL in en geef de stream een naam.',
        action: { type: 'copy', field: 'website_url' },
        tip: 'Gebruik de URL zonder trailing slash, bijv. "https://www.uwsite.nl"',
      },
      {
        title: 'Tracking code installeren',
        description: 'Kopieer de Measurement ID (begint met G-). Installeer deze in uw website:\n\n• WordPress: installeer plugin "Site Kit by Google" en volg de wizard\n• Wix: Marketing → Integraties → Google Analytics → plak uw ID\n• Squarespace: Instellingen → Geavanceerd → Externe services → Google Analytics',
        action: { type: 'check' },
        tip: 'De plugin "Site Kit by Google" is de makkelijkste manier voor WordPress.',
      },
      {
        title: 'Controleer of data binnenkomt',
        description: 'Ga naar Google Analytics → Rapporten → Realtime. Open uw website in een ander tabblad. U zou uzelf als bezoeker moeten zien verschijnen.',
        action: { type: 'link', url: 'https://analytics.google.com/', label: 'Controleer Realtime' },
        tip: 'Het kan 24-48 uur duren voordat historische rapporten beschikbaar zijn.',
      },
    ],
  },
  ssl_ontbreekt: {
    title: 'SSL-certificaat (HTTPS) Activeren',
    subtitle: 'Beveilig uw website en verdien meer vertrouwen',
    estimated_time: '1-2 uur (meestal door hosting provider)',
    steps: [
      {
        title: 'Contact opnemen met uw hosting provider',
        description: 'Stuur een e-mail of bel uw hosting provider met het verzoek om een SSL-certificaat te activeren.',
        action: { type: 'copy', field: 'ssl_verzoek' },
        tip: 'De meeste hosting providers bieden gratis Let\'s Encrypt SSL aan.',
      },
      {
        title: 'Wachten op activatie',
        description: 'De hosting provider activeert het certificaat. Dit duurt meestal 1-24 uur. U ontvangt een bevestiging per e-mail.',
        action: { type: 'check' },
      },
      {
        title: 'HTTP → HTTPS redirect instellen',
        description: 'Vraag uw hosting provider om alle HTTP verkeer automatisch door te sturen naar HTTPS. Dit is essentieel om te voorkomen dat bezoekers op de onbeveiligde versie terechtkomen.',
        action: { type: 'check' },
      },
      {
        title: 'Controleren',
        description: 'Open uw website in een browser. U zou een slotje moeten zien in de adresbalk. Controleer ook of de HTTP-versie automatisch doorgestuurd wordt.',
        action: { type: 'check' },
        tip: 'Test zowel http://uwsite.nl als https://uwsite.nl',
      },
    ],
  },
  reviews_strategie: {
    title: 'Review-strategie Opzetten',
    subtitle: 'Verzamel systematisch Google reviews',
    estimated_time: '15 minuten setup, daarna doorlopend',
    steps: [
      {
        title: 'Review-link aanmaken',
        description: 'Open uw Google Bedrijfsprofiel en kopieer uw directe review-link. Klanten kunnen via deze link direct een review achterlaten.',
        action: { type: 'link', url: 'https://business.google.com/', label: 'Open Google Business' },
        tip: 'Zoek naar "Meer reviews krijgen" of "Reviewlink delen" in uw profiel.',
      },
      {
        title: 'WhatsApp/email template klaarmaken',
        description: 'Maak een kort, vriendelijk bericht dat u na elk afgerond project kunt sturen.',
        action: { type: 'copy', field: 'review_bericht' },
      },
      {
        title: 'Contacten op een lijst zetten',
        description: 'Maak een lijst van uw meest tevreden klanten van de afgelopen 6 maanden. Begin met hen een review te vragen.',
        action: { type: 'check' },
        tip: 'Stuur maximaal 5 verzoeken per dag om niet als spam over te komen.',
      },
      {
        title: 'Routine instellen',
        description: 'Maak er een gewoonte van: stuur na elk afgerond project binnen 24 uur een review-verzoek. Stel een herinnering in uw agenda.',
        action: { type: 'check' },
        tip: 'Doel: minimaal 1-2 nieuwe reviews per week.',
      },
    ],
  },
  geen_social: {
    title: 'Social Media Profielen Aanmaken',
    subtitle: 'Word zichtbaar op de platforms waar uw klanten zijn',
    estimated_time: '1-2 uur',
    steps: [
      {
        title: 'Kies uw platforms',
        description: 'Niet elk platform is voor elk bedrijf geschikt:\n\n• B2C (particulieren): Instagram + Facebook\n• B2B (zakelijk): LinkedIn + Facebook\n• Visuele producten/diensten: Instagram + Pinterest\n• Jong publiek: TikTok + Instagram',
        action: { type: 'check' },
      },
      {
        title: 'Facebook bedrijfspagina aanmaken',
        description: 'Maak een Facebook Business Page aan. Dit is ook nodig voor Instagram Business.',
        action: { type: 'link', url: 'https://www.facebook.com/pages/create', label: 'Maak Facebook Pagina' },
      },
      {
        title: 'Instagram Business account',
        description: 'Download de Instagram app, maak een account aan en schakel over naar een Business account (via Instellingen → Account → Overschakelen naar zakelijk account).',
        action: { type: 'link', url: 'https://www.instagram.com/', label: 'Open Instagram' },
        tip: 'Koppel uw Instagram aan uw Facebook pagina voor gedeeld beheer.',
      },
      {
        title: 'Profielen volledig invullen',
        description: 'Vul alle velden in: profielfoto (logo), omslagfoto, bio/beschrijving, website URL, contactgegevens en openingstijden.',
        action: { type: 'copy', field: 'social_bio' },
      },
      {
        title: 'Eerste 3 berichten plaatsen',
        description: 'Post minimaal 3 berichten zodat uw profiel niet leeg oogt. Ideeën: een introductie, een foto van uw werk/product, een klantverhaal of tip.',
        action: { type: 'check' },
        tip: 'Plan daarna 2-3 berichten per week. Gebruik de gratis tool Canva voor mooie visuals.',
      },
    ],
  },
  email_marketing: {
    title: 'Email Marketing Opzetten',
    subtitle: 'Het kanaal met de hoogste ROI: €42 per €1',
    estimated_time: '2-3 uur',
    steps: [
      {
        title: 'Account aanmaken bij email platform',
        description: 'Maak een gratis account aan bij Mailchimp (tot 500 contacten gratis) of Brevo (tot 300 emails/dag gratis).',
        action: { type: 'link', url: 'https://mailchimp.com/nl/', label: 'Open Mailchimp' },
        tip: 'Brevo (brevo.com) is ook een goede optie met onbeperkte contacten.',
      },
      {
        title: 'Aanmeldformulier maken',
        description: 'Maak een inschrijfformulier en plaats het op uw website. De meeste email platforms bieden een embed-code of WordPress plugin.',
        action: { type: 'check' },
      },
      {
        title: 'Welkomstmail instellen',
        description: 'Maak een automatische welkomstmail die verstuurd wordt zodra iemand zich aanmeldt.',
        action: { type: 'copy', field: 'welkomstmail' },
      },
      {
        title: 'Eerste nieuwsbrief versturen',
        description: 'Stuur uw eerste nieuwsbrief met een tip, aanbieding of bedrijfsnieuws. Houd het kort en waardevol.',
        action: { type: 'check' },
        tip: 'Plan om maandelijks een nieuwsbrief te versturen.',
      },
    ],
  },
  lokale_citaties: {
    title: 'Lokale Citaties Opbouwen',
    subtitle: 'Versterk uw lokale vindbaarheid met consistente vermeldingen',
    estimated_time: '2-3 uur',
    steps: [
      {
        title: 'NAW-gegevens voorbereiden',
        description: 'Zorg dat u overal exact dezelfde bedrijfsinformatie gebruikt. Kopieer onderstaande gegevens.',
        action: { type: 'copy', field: 'naw_gegevens' },
        tip: 'Consistentie is cruciaal. Gebruik overal exact dezelfde schrijfwijze.',
      },
      {
        title: 'Registreer bij Google Bedrijfsprofiel',
        description: 'Dit is de belangrijkste vermelding. Als u dit al gedaan heeft, ga door naar de volgende stap.',
        action: { type: 'link', url: 'https://business.google.com/', label: 'Google Business' },
      },
      {
        title: 'Registreer bij Yelp',
        description: 'Claim uw bedrijfspagina op Yelp en vul alle gegevens in.',
        action: { type: 'link', url: 'https://biz.yelp.nl/', label: 'Open Yelp Business' },
      },
      {
        title: 'Registreer bij overige platforms',
        description: 'Meld uw bedrijf aan bij: Telefoonboek.nl, Gouden Gids (goudengids.nl), Trustpilot (business.trustpilot.com), Apple Maps (mapsconnect.apple.com).',
        action: { type: 'check' },
      },
      {
        title: 'Controleer consistentie',
        description: 'Zoek uw bedrijfsnaam op Google en controleer of alle vermeldingen dezelfde NAW-gegevens tonen.',
        action: { type: 'check' },
        tip: 'Stel een kwartaalherinnering in om uw vermeldingen te controleren.',
      },
    ],
  },
};

/**
 * Haalt de dynamische copy-tekst op basis van het veld en de opdracht data.
 */
function getCopyText(field, opdracht) {
  const examples = opdracht?.diy_examples || [];
  const firstExample = examples[0];

  const map = {
    company: opdracht?._company || 'Uw bedrijfsnaam',
    branche: opdracht?._branche || 'Uw branche',
    locatie: opdracht?._locatie || 'Uw locatie',
    website_url: opdracht?._website_url || 'https://www.uwwebsite.nl',
    gbp_beschrijving: firstExample?.text || `${opdracht?._company || 'Uw bedrijf'} is uw specialist in ${opdracht?._branche || 'uw branche'} in ${opdracht?._locatie || 'uw regio'}. Wij bieden persoonlijke service, professioneel advies en snelle levering.`,
    ssl_verzoek: `Geachte hosting provider, ik wil graag een SSL-certificaat (HTTPS) activeren voor mijn website. Kunt u een gratis Let's Encrypt certificaat installeren en alle HTTP-verkeer doorsturen naar HTTPS? Bij voorbaat dank.`,
    review_bericht: firstExample?.text || `Beste [klantnaam], bedankt voor uw vertrouwen in ${opdracht?._company || 'ons'}! Wij zouden het zeer waarderen als u een korte review achterlaat: [uw Google review link]. Alvast bedankt!`,
    social_bio: firstExample?.text || `${opdracht?._company || 'Bedrijf'} | ${(opdracht?._branche || 'Branche').charAt(0).toUpperCase() + (opdracht?._branche || 'Branche').slice(1)} in ${opdracht?._locatie || 'uw regio'}\nPersoonlijke service & expertise\nVraag vrijblijvend een offerte aan`,
    welkomstmail: firstExample?.text || `Onderwerp: Welkom bij ${opdracht?._company || 'ons'}!\n\nBedankt voor uw aanmelding! Wij delen graag tips en exclusieve aanbiedingen.\n\nMet vriendelijke groet,\n${opdracht?._company || 'Het team'}`,
    naw_gegevens: firstExample?.text || `Bedrijfsnaam: ${opdracht?._company || '[uw bedrijfsnaam]'}\nAdres: [straat + huisnummer], [postcode] ${opdracht?._locatie || '[plaats]'}\nTelefoon: [uw telefoonnummer]\nWebsite: ${opdracht?._website_url || '[uw website]'}`,
  };

  return map[field] || '';
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      data-testid="guided-copy-btn"
      onClick={handleCopy}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '4px',
        padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 600,
        background: copied ? '#059669' : '#ecfdf5', color: copied ? 'white' : '#059669',
        border: `1px solid ${copied ? '#059669' : '#a7f3d0'}`, cursor: 'pointer',
        transition: 'all 0.2s ease',
      }}
    >
      {copied ? <><CheckCircle size={12} /> Gekopieerd!</> : <><Copy size={12} /> Kopieer tekst</>}
    </button>
  );
}

export default function GuidedFlowModal({ opdracht, onClose }) {
  const flowDef = GUIDED_FLOWS[opdracht?.template_id];
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSteps, setCompletedSteps] = useState(new Set());

  if (!flowDef) return null;

  const step = flowDef.steps[currentStep];
  const isLastStep = currentStep === flowDef.steps.length - 1;
  const isFirstStep = currentStep === 0;
  const progress = Math.round(((completedSteps.size) / flowDef.steps.length) * 100);

  const toggleStep = (idx) => {
    setCompletedSteps(prev => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const goNext = () => {
    toggleStep(currentStep);
    if (!isLastStep) setCurrentStep(s => s + 1);
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 100, display: 'flex',
      alignItems: 'center', justifyContent: 'center', padding: '20px',
    }}>
      <div style={{
        position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)',
        backdropFilter: 'blur(6px)',
      }} onClick={onClose} />

      <div data-testid="guided-flow-modal" style={{
        position: 'relative', background: 'white', borderRadius: '20px',
        maxWidth: '640px', width: '100%', maxHeight: '90vh', overflow: 'hidden',
        boxShadow: '0 24px 48px rgba(0,0,0,0.2)',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '24px 24px 16px', borderBottom: '1px solid #f1f5f9',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                <Sparkles size={16} style={{ color: '#D4A017' }} />
                <span style={{ fontSize: '11px', fontWeight: 700, color: '#D4A017', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Guided Setup
                </span>
              </div>
              <h2 style={{ fontFamily: "'Sora', sans-serif", fontSize: '20px', fontWeight: 800, color: '#0f172a', marginBottom: '2px' }}>
                {flowDef.title}
              </h2>
              <p style={{ fontSize: '13px', color: '#64748b' }}>{flowDef.subtitle}</p>
            </div>
            <button onClick={onClose} style={{
              background: '#f8fafc', border: 'none', borderRadius: '8px',
              width: '32px', height: '32px', cursor: 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
            }}>
              <X size={16} style={{ color: '#64748b' }} />
            </button>
          </div>

          {/* Progress */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ flex: 1, height: '4px', background: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
              <div style={{
                width: `${progress}%`, height: '100%', background: '#059669',
                borderRadius: '4px', transition: 'width 0.3s ease',
              }} />
            </div>
            <span style={{ fontSize: '11px', fontWeight: 700, color: '#059669', whiteSpace: 'nowrap' }}>
              {completedSteps.size}/{flowDef.steps.length}
            </span>
          </div>
        </div>

        {/* Step navigation sidebar + content */}
        <div style={{ flex: 1, overflow: 'auto', display: 'flex' }}>
          {/* Step list sidebar */}
          <div style={{
            width: '200px', flexShrink: 0, padding: '16px 12px', borderRight: '1px solid #f1f5f9',
            background: '#fafbfc', overflowY: 'auto',
          }}>
            {flowDef.steps.map((s, i) => (
              <button
                key={i}
                data-testid={`guided-step-nav-${i}`}
                onClick={() => setCurrentStep(i)}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '8px', width: '100%',
                  padding: '8px 10px', borderRadius: '8px', border: 'none', cursor: 'pointer',
                  background: currentStep === i ? 'white' : 'transparent',
                  boxShadow: currentStep === i ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                  marginBottom: '4px', textAlign: 'left',
                  transition: 'all 0.15s ease',
                }}
              >
                {completedSteps.has(i) ? (
                  <CheckCircle size={16} style={{ color: '#059669', flexShrink: 0, marginTop: '1px' }} />
                ) : (
                  <Circle size={16} style={{ color: currentStep === i ? '#D4A017' : '#d1d5db', flexShrink: 0, marginTop: '1px' }} />
                )}
                <span style={{
                  fontSize: '11px', fontWeight: currentStep === i ? 700 : 500,
                  color: completedSteps.has(i) ? '#059669' : (currentStep === i ? '#0f172a' : '#64748b'),
                  lineHeight: 1.3,
                }}>
                  {s.title}
                </span>
              </button>
            ))}
          </div>

          {/* Step content */}
          <div style={{ flex: 1, padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <div style={{
                width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
                background: completedSteps.has(currentStep) ? '#059669' : '#0f172a',
                color: completedSteps.has(currentStep) ? 'white' : '#D4A017',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '12px', fontWeight: 800,
              }}>
                {completedSteps.has(currentStep) ? <CheckCircle size={14} /> : currentStep + 1}
              </div>
              <h3 style={{ fontFamily: "'Sora', sans-serif", fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                {step.title}
              </h3>
            </div>

            <p style={{ fontSize: '14px', color: '#374151', lineHeight: 1.7, marginBottom: '16px', whiteSpace: 'pre-line' }}>
              {step.description}
            </p>

            {/* Action area */}
            {step.action?.type === 'link' && (
              <a
                href={step.action.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="guided-external-link"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  padding: '10px 18px', borderRadius: '10px', fontSize: '13px', fontWeight: 700,
                  background: '#0f172a', color: 'white', textDecoration: 'none',
                  marginBottom: '14px',
                }}
              >
                <ExternalLink size={14} /> {step.action.label}
              </a>
            )}

            {step.action?.type === 'copy' && (
              <div style={{
                background: '#f0fdf4', borderRadius: '10px', border: '1px dashed #a7f3d0',
                padding: '12px', marginBottom: '14px',
              }}>
                <p style={{
                  fontSize: '13px', color: '#0f172a', lineHeight: 1.6,
                  whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginBottom: '10px',
                  fontFamily: "'Sora', monospace",
                }}>
                  {getCopyText(step.action.field, opdracht)}
                </p>
                <CopyButton text={getCopyText(step.action.field, opdracht)} />
              </div>
            )}

            {/* Tip */}
            {step.tip && (
              <div style={{
                background: 'rgba(212,160,23,0.06)', borderRadius: '10px', padding: '12px 14px',
                border: '1px solid rgba(212,160,23,0.15)', marginBottom: '14px',
                display: 'flex', gap: '8px',
              }}>
                <Sparkles size={14} style={{ color: '#D4A017', flexShrink: 0, marginTop: '2px' }} />
                <p style={{ fontSize: '12px', color: '#64748b', lineHeight: 1.5 }}>
                  <strong style={{ color: '#D4A017' }}>Tip:</strong> {step.tip}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer navigation */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid #f1f5f9',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Clock size={13} style={{ color: '#94a3b8' }} />
            <span style={{ fontSize: '11px', color: '#94a3b8' }}>{flowDef.estimated_time}</span>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            {!isFirstStep && (
              <button
                data-testid="guided-prev-btn"
                onClick={() => setCurrentStep(s => s - 1)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '4px',
                  padding: '8px 16px', borderRadius: '8px', border: '1px solid #e2e8f0',
                  background: 'white', cursor: 'pointer', fontSize: '12px', fontWeight: 600, color: '#64748b',
                }}
              >
                <ArrowLeft size={14} /> Vorige
              </button>
            )}

            <button
              data-testid="guided-complete-step-btn"
              onClick={goNext}
              style={{
                display: 'flex', alignItems: 'center', gap: '4px',
                padding: '8px 16px', borderRadius: '8px', border: 'none',
                background: completedSteps.has(currentStep)
                  ? '#059669'
                  : (isLastStep ? '#059669' : '#0f172a'),
                color: 'white', cursor: 'pointer', fontSize: '12px', fontWeight: 700,
              }}
            >
              {completedSteps.has(currentStep) ? (
                isLastStep ? <><CheckCircle size={14} /> Afgerond!</> : <><CheckCircle size={14} /> Volgende</>
              ) : (
                isLastStep ? <><CheckCircle size={14} /> Afronden</> : <>Voltooid &amp; volgende <ChevronRight size={14} /></>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Check of een opdracht een guided flow heeft */
export function hasGuidedFlow(templateId) {
  return !!GUIDED_FLOWS[templateId];
}
